import React from 'react';

interface FormattedPreviewProps {
  content: string;
  platform: string;
}

export default function FormattedPreview({ content, platform }: FormattedPreviewProps) {
  // Convert formatting to HTML for preview
  const formatContent = (text: string, platform: string) => {
    let formatted = text;

    // First, handle line breaks by converting them to <br> tags
    formatted = formatted.replace(/\n/g, '<br>');

    switch (platform) {
      case 'whatsapp':
        // WhatsApp formatting
        formatted = formatted
          .replace(/\*(.*?)\*/g, '<strong>$1</strong>') // Bold
          .replace(/_(.*?)_/g, '<em>$1</em>') // Italic
          .replace(/~(.*?)~/g, '<del>$1</del>') // Strikethrough
          .replace(/`(.*?)`/g, '<code>$1</code>'); // Monospace
        break;
      
      case 'telegram':
        // Telegram formatting
        formatted = formatted
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
          .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
          .replace(/__(.*?)__/g, '<u>$1</u>') // Underline
          .replace(/~~(.*?)~~/g, '<del>$1</del>') // Strikethrough
          .replace(/`(.*?)`/g, '<code>$1</code>') // Monospace
          .replace(/```(.*?)```/gs, '<pre><code>$1</code></pre>') // Code blocks
          .replace(/<small>(.*?)<\/small>/g, '<span style="font-size: 0.875em;">$1</span>') // Small text
          .replace(/<b>(.*?)<\/b>/g, '<strong>$1</strong>') // Bold
          .replace(/<u>(.*?)<\/u>/g, '<u>$1</u>') // Underline
          .replace(/<code>(.*?)<\/code>/g, '<code style="background: #f1f1f1; padding: 2px 4px; border-radius: 3px;">$1</code>') // Code
          .replace(/<pre>(.*?)<\/pre>/g, '<pre style="background: #f1f1f1; padding: 8px; border-radius: 4px; overflow-x: auto;">$1</pre>'); // Pre
        break;
      
      case 'instagram':
      case 'facebook':
        // Instagram and Facebook don't support text formatting, but show basic formatting for preview
        formatted = formatted
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
          .replace(/\*(.*?)\*/g, '<em>$1</em>'); // Italic
        break;
    }

    return formatted;
  };

  const getPlatformInfo = (platform: string) => {
    switch (platform) {
      case 'whatsapp':
        return {
          name: 'WhatsApp',
          icon: '💬',
          color: 'bg-green-500',
          maxLength: 1000,
        };
      case 'telegram':
        return {
          name: 'Telegram',
          icon: '📡',
          color: 'bg-cyan-500',
          maxLength: 4000,
        };
      case 'instagram':
        return {
          name: 'Instagram',
          icon: '📷',
          color: 'bg-pink-500',
          maxLength: 2200,
        };
      case 'facebook':
        return {
          name: 'Facebook',
          icon: '📘',
          color: 'bg-cyan-600',
          maxLength: 5000,
        };
      default:
        return {
          name: 'Unknown',
          icon: '📄',
          color: 'bg-slate-800',
          maxLength: 1000,
        };
    }
  };

  const platformInfo = getPlatformInfo(platform);
  const formattedContent = formatContent(content, platform);
  // Count characters including line breaks (they count as 1 character each)
  const characterCount = content.length;
  const lineBreakCount = (content.match(/\n/g) || []).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-lg">{platformInfo.icon}</span>
        <h4 className="font-medium text-slate-900">{platformInfo.name} Preview</h4>
        <span className={`px-2 py-1 text-xs text-white rounded-full ${platformInfo.color}`}>
          {characterCount}/{platformInfo.maxLength}
          {lineBreakCount > 0 && (
            <span className="ml-1 text-xs opacity-75">
              ({lineBreakCount} breaks)
            </span>
          )}
        </span>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <div 
          className="prose prose-sm max-w-none whitespace-pre-wrap"
          style={{ 
            lineHeight: '1.5',
            fontFamily: platform === 'whatsapp' ? 'system-ui, -apple-system, sans-serif' : 'inherit'
          }}
          dangerouslySetInnerHTML={{ __html: formattedContent || '<span class="text-slate-400">No content to preview</span>' }}
        />
      </div>
      
      {/* Platform-specific notes */}
      <div className="space-y-2">
        {platform === 'instagram' || platform === 'facebook' ? (
          <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
            <strong>Note:</strong> {platformInfo.name} doesn't support text formatting in captions/posts. 
            Formatting shown here is for preview purposes only.
          </div>
        ) : null}
        
        {/* Line break information */}
        <div className="p-2 bg-cyan-50 border border-blue-200 rounded text-xs text-cyan-700">
          <strong>Line Breaks:</strong> Press Enter in the editor to create line breaks. 
          Each line break counts as 1 character toward the limit.
        </div>
      </div>
    </div>
  );
}
